import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getSavedContracts } from '../services/favorites';

export interface Notification {
    id: string;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'success' | 'error';
    date: string;
    read: boolean;
    link?: string;
    contractId?: string;
}

interface NotificationsContextType {
    notifications: Notification[];
    unreadCount: number;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    deleteNotification: (id: string) => void;
    addNotification: (notification: Omit<Notification, 'id' | 'date' | 'read'>) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: ReactNode }) {
    const { user, appUser } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);

    // Load notifications from local storage on mount (persisted per user)
    useEffect(() => {
        if (user) {
            const saved = localStorage.getItem(`notifications_${user.uid}`);
            if (saved) {
                setNotifications(JSON.parse(saved));
            }
        } else {
            setNotifications([]);
        }
    }, [user]);

    // Persist to local storage whenever notifications change
    useEffect(() => {
        if (user) {
            localStorage.setItem(`notifications_${user.uid}`, JSON.stringify(notifications));
        }
    }, [notifications, user]);

    // Check for deadline reminders
    useEffect(() => {
        if (!user || !appUser?.contractorId) return;

        const checkDeadlines = async () => {
            try {
                // Fetch saved contracts for the current contractor
                const savedContracts = await getSavedContracts(appUser.contractorId!);
                const now = new Date();
                const threeDaysFromNow = new Date();
                threeDaysFromNow.setDate(now.getDate() + 3);

                const newNotifications: Notification[] = [];

                savedContracts.forEach(contract => {
                    if (!contract.responseDeadline) return;

                    const deadline = new Date(contract.responseDeadline);

                    // Check if deadline is within the next 3 days and hasn't passed
                    if (deadline > now && deadline <= threeDaysFromNow) {
                        const notifId = `deadline_${contract.id}_${deadline.toISOString().split('T')[0]}`;

                        // Avoid duplicates
                        const exists = notifications.some(n => n.id === notifId);
                        if (!exists) {
                            const daysLeft = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

                            newNotifications.push({
                                id: notifId,
                                title: 'Deadline Approaching',
                                message: `Contract "${contract.title}" closes in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`,
                                type: 'warning',
                                date: new Date().toISOString(),
                                read: false,
                                link: contract.uiLink,
                                contractId: contract.id
                            });
                        }
                    }
                });

                if (newNotifications.length > 0) {
                    setNotifications(prev => [...newNotifications, ...prev]);
                }

            } catch (error) {
                console.error("Failed to check deadlines:", error);
            }
        };

        // Run check immediately and then every hour
        checkDeadlines();
        const interval = setInterval(checkDeadlines, 60 * 60 * 1000);

        return () => clearInterval(interval);
    }, [user, appUser, notifications]); // Added notifications to dependency to check duplication correctly

    const unreadCount = notifications.filter(n => !n.read).length;

    const markAsRead = (id: string) => {
        setNotifications(prev => prev.map(n =>
            n.id === id ? { ...n, read: true } : n
        ));
    };

    const markAllAsRead = () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };

    const deleteNotification = (id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const addNotification = (notification: Omit<Notification, 'id' | 'date' | 'read'>) => {
        const newNotif: Notification = {
            ...notification,
            id: crypto.randomUUID(),
            date: new Date().toISOString(),
            read: false
        };
        setNotifications(prev => [newNotif, ...prev]);
    };

    return (
        <NotificationsContext.Provider value={{
            notifications,
            unreadCount,
            markAsRead,
            markAllAsRead,
            deleteNotification,
            addNotification
        }}>
            {children}
        </NotificationsContext.Provider>
    );
}

export function useNotifications() {
    const context = useContext(NotificationsContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
}
